import { useBreakpoints } from '@/lib/device/useBreakpoint'
import type React from 'react'
import { useCallback, useEffect, useRef } from 'react'
import { useDebounceCallback } from 'usehooks-ts'

const MENU_HEIGHT = 140
const SHRINK_HEADER_DEBOUNCE_MS = 80

let headerHeightMobile = 0
let headerHeightDesktop = 0

/**
 * Calculates and sets the header height CSS variables.
 *
 * @param isMenuOpen Whether or not the menu is open.
 * @param liveHeaderHeight The height of the header including banner.
 */
function calculateHeaderHeight(isMenuOpen: boolean, liveHeaderHeight: number): void {
  if (liveHeaderHeight === 0) {
    return
  }

  headerHeightDesktop = isMenuOpen ? liveHeaderHeight + MENU_HEIGHT : liveHeaderHeight
  headerHeightMobile = liveHeaderHeight

  document.documentElement.style.setProperty(
    '--app-header-height-desktop',
    `${headerHeightDesktop}px`,
  )
  document.documentElement.style.setProperty('--app-header-height', `${headerHeightMobile}px`)
}

export const useHeaderScroll = (
  headerRef: React.RefObject<HTMLDivElement | null>,
  liveHeaderHeight: number, // header + banner height
  isMenuOpen: boolean,
  setTabContentVisible: React.Dispatch<React.SetStateAction<boolean>>,
  pathname: string,
) => {
  const lastScrollTop = useRef<number>(0)
  const { isDesktopWidth } = useBreakpoints()

  // Reset state when pathname changes
  useEffect(() => {
    lastScrollTop.current = 0
    headerRef.current?.removeAttribute('data-scrolled-beyond')
  }, [pathname, headerRef])

  const shrinkHeader = useCallback(
    (scrollDifference: number) => {
      headerRef.current?.setAttribute('data-scrolled-beyond', 'true')

      // Show full header when scrolling up
      if (scrollDifference < 0) {
        headerRef.current?.removeAttribute('data-scrolled-beyond')
      }
    },
    [headerRef],
  )

  // NOTE: useDebouncedCallback returns a new function on each render, so we memoize it to avoid
  // unnecessary re-renders in handleScroll's dependency array
  const shrinkHeaderDebounced = useDebounceCallback(shrinkHeader, SHRINK_HEADER_DEBOUNCE_MS)

  const handleScroll = useCallback(() => {
    const headerHeight = isDesktopWidth ? headerHeightDesktop : headerHeightMobile

    // Allow hiding the header when scrolling down past two-thirds of the header height.
    const hasScrolledBeyond = window.scrollY > (headerHeight / 3) * 2
    const scrollTop = window.scrollY
    const scrollDifference = scrollTop - lastScrollTop.current

    if (Math.abs(scrollTop) > MENU_HEIGHT) {
      if (scrollDifference > 0) {
        setTabContentVisible(false)
      } else {
        setTabContentVisible(true)
      }
    }

    if (scrollDifference < 0) {
      document.documentElement.style.setProperty('--scroll-padding-top', `${headerHeight}px`)
    } else {
      document.documentElement.style.setProperty('--scroll-padding-top', '0')
    }

    if (hasScrolledBeyond) {
      shrinkHeaderDebounced(scrollDifference)
    } else {
      headerRef.current?.removeAttribute('data-scrolled-beyond')
    }

    lastScrollTop.current = scrollTop
  }, [headerRef, isDesktopWidth, setTabContentVisible, shrinkHeaderDebounced])

  useEffect(() => {
    handleScroll()
    window.addEventListener('scroll', handleScroll)

    return () => {
      window.removeEventListener('scroll', handleScroll)
    }
  }, [handleScroll])

  useEffect(() => {
    calculateHeaderHeight(isMenuOpen, liveHeaderHeight)

    return () => {
      document.documentElement.style.removeProperty('--app-header-height-desktop')
      document.documentElement.style.removeProperty('--app-header-height')
    }
  }, [liveHeaderHeight, isMenuOpen])
}
